/* Choosing the language — STORY-042, reachable from anywhere — STORY-043.
 *
 * ── endonyms, and that is the whole design of it ────────────────────────────
 * "Türkçe", never "Turkish". This is the one control most likely to be read by
 * somebody who cannot read the language the screen is currently in: they are
 * looking for their own language precisely because they cannot follow the rest
 * of the page. A list translated into the current language is no help to them.
 *
 * The names therefore do NOT come from the catalogue. The label does, because
 * somebody who can read the screen should be told what the control is for.
 *
 * ── a real `<select>` ───────────────────────────────────────────────────────
 * Two options. `design.md` prefers the real control at this size: keyboard
 * accessible, screen-reader friendly, and correct on mobile for free.
 *
 * ── two shapes, one control ─────────────────────────────────────────────────
 * The same reasoning put it in two places, and each has room for a different
 * amount of it. Below an auth card and on Account there is a label and space
 * for the failure to be explained in a sentence; in the workspace topbar there
 * is a bar, so the name is on the control and the sentence hangs beneath it.
 *
 * They are one component rather than two because they are one behaviour: the
 * value, the write, and what happens when the write fails must not be able to
 * differ by where somebody happened to change their language.
 */

import type { Locale } from '@eventuley/i18n';
import { LOCALES } from '@eventuley/i18n';
import { useId } from 'react';
import { useLocaleControl } from '../../app/locale.js';
import { useTranslate } from '../i18n/index.js';
import { LOCALE_ENDONYMS } from '../i18n/preference.js';
import { Callout } from './callout.js';

export interface LanguagePickerProps {
  /**
   * `field` — a labelled field, for a form column. The default.
   *
   * `bar` — the bare control in the workspace topbar, named by `aria-label`
   * because there is no room for a visible label and the options say what it
   * is in the only language that matters to the person using it.
   */
  readonly variant?: 'field' | 'bar';
}

export function LanguagePicker({ variant = 'field' }: LanguagePickerProps) {
  const t = useTranslate();
  const { locale, saving, error, choose } = useLocaleControl();
  const id = useId();

  const options = LOCALES.map((option) => (
    /* `lang` on the option, so a screen reader pronounces "Türkçe" as Turkish
       rather than attempting it in English. */
    <option key={option} value={option} lang={option}>
      {LOCALE_ENDONYMS[option]}
    </option>
  ));

  const onChange = (changed: React.ChangeEvent<HTMLSelectElement>) =>
    choose(changed.target.value as Locale);

  if (variant === 'bar') {
    return (
      <span className="topbar__lang-group">
        <select
          className="topbar__lang"
          /* The name a screen reader announces. It comes from the catalogue and
             is therefore in the current language — which is right, because the
             voice reading it is also in the current language. The way OUT for
             somebody who cannot read the screen is the option list, and that is
             endonyms. */
          aria-label={t('language.label')}
          value={locale}
          disabled={saving}
          onChange={onChange}
        >
          {options}
        </select>

        {error ? (
          /* Hung under the control rather than placed in the bar: the sentence
             is a sentence, and a topbar has no room for one. Absolutely
             positioned so nothing reflows when it appears — a failure notice
             that moves the whole shell reads as a second fault. */
          <div className="topbar__lang-notice">
            <Callout tone="warning">{t('language.not_saved')}</Callout>
          </div>
        ) : null}
      </span>
    );
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {t('language.label')}
      </label>
      <select
        id={id}
        className="field__control"
        value={locale}
        disabled={saving}
        onChange={onChange}
      >
        {options}
      </select>

      {error ? (
        /* The screen is already in the chosen language and stays there. Saying
           the preference did not stick is the honest half — a preference that
           appears to save and does not is worse than a refusal. */
        <Callout tone="warning">{t('language.not_saved')}</Callout>
      ) : null}
    </div>
  );
}
